import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLanguage } from '../../../../utils/i18n';

export default function RepairTypeInfo({ open, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useLanguage();
  const repairTypes = [
    {
      category: t('repair_type.categoryStructuralLeakRepair', 'Structural & Leak Repair'),
      items: [
        {
          title: 'Patch Repair',
          description: t(
            'repair_type.descriptionPatchRepair',
            'Application of special reinforcing rubber patches (radial/bias patches) on the inside of the tire casing to seal puncture holes and hold air pressure.'
          )
        },
        {
          title: 'Section Repair',
          description: t(
            'repair_type.descriptionSectionRepair',
            'Cutting and cleaning of the tire area experiencing broken steel cords or nylon threads, which is then rewoven/reinforced before being patched and cured.'
          )
        },
        {
          title: 'Bead Repair',
          description: t(
            'repair_type.descriptionBeadRepair',
            'Restoration of damage to the rubber enclosing the stiff wire (bead) at the edge of the tire that attaches to the rim, caused by improper mounting/dismounting or overloading.'
          )
        },
        {
          title: 'Spot / Cosmetic Repair',
          description: t(
            'repair_type.descriptionSpotCosmeticRepair',
            'Filling of raw rubber into external cuts that have not penetrated the cords, aimed at preventing mine water from entering and causing the cords to rust.'
          )
        }
      ]
    },
    {
      category: t('repair_type.categoryTreadReconditioning', 'Tread Reconditioning & Modification'),
      items: [
        {
          title: 'Retread',
          description: t(
            'repair_type.descriptionRetread',
            'Replacement of the entire worn-out tire tread layer with a new tread layer so that the old tire casing can be reused.'
          )
        },
        {
          title: 'Vulkanisir (Vulcanization)',
          description: t(
            'repair_type.descriptionVulcanization',
            'A method of bonding new rubber (raw rubber) to the original tire using a combination of heat, pressure, and time (both hot/mold-cure and cold/pre-cure vulcanization systems).'
          )
        },
        {
          title: 'Regrooving',
          description: t(
            'repair_type.descriptionRegrooving',
            "Recarving the thinning tire tread grooves using a special heated knife, applicable only for tires marked 'REGROOVABLE'."
          )
        }
      ]
    },
    {
      category: t('repair_type.categoryProtectionPreventionMechanical', 'Protection, Prevention, & Mechanical Methods'),
      items: [
        {
          title: 'Tyre Liquid Sealant Injection',
          description: t(
            'repair_type.descriptionLiquidSealant',
            'Injection of a special gel liquid into tubeless tires that will instantly and automatically seal holes if the tire is punctured by sharp rocks during operation.'
          )
        },
        {
          title: 'Sidewall Reinforcement',
          description: t(
            'repair_type.descriptionSidewall',
            'Addition of an extra protective rubber layer to the tire sidewall to withstand extreme friction against mining pit walls.'
          )
        },
        {
          title: 'Skiving and Buffing',
          description: t(
            'repair_type.descriptionSkiving',
            'The mechanical process of peeling off damaged rubber, removing rusted wires, and smoothing the tire surface using a high-speed air grinder before the patching stage.'
          )
        }
      ]
    }
  ];

  const filteredCategories = repairTypes
    .map((category) => {
      const filteredItems = category.items.filter(
        (item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return { ...category, items: filteredItems };
    })
    .filter((category) => category.items.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>{t('tireCardHistory.repairType', 'Repair Type Information')}</DialogTitle>
      <Box sx={{ px: 3, pb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('tireRepairScrap.searchRepairType', 'Search repair type (e.g. Retread)...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Box>
      <DialogContent dividers>
        {filteredCategories.length > 0 ? (
          filteredCategories.map((category, idx) => (
            <Box key={category.category} sx={{ mb: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                {category.category}
              </Typography>
              {category.items.map((item, itemIdx) => (
                <Box key={item.title} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {item.title}:
                  </Typography>
                  <Typography variant="body2">{item.description}</Typography>
                </Box>
              ))}
              {idx < filteredCategories.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            {t('tireRepairScrap.noRepairTypeFound', 'No repair types found matching')} "{searchQuery}"
          </Typography>
        )}

        {/* Always show Others description if no search or if it matches */}
        {('others'.includes(searchQuery.toLowerCase()) || searchQuery === '') && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" color="primary" gutterBottom>
              {t('tireRepairScrap.functionOfOthers', 'Function of the "Others.." Option')}
            </Typography>
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{
                __html: t(
                  'tireRepairScrap.othersDescription',
                  'Use the <strong>Others..</strong> option if the type of repair performed is not listed above. You are required to manually enter the name of the repair using correct English in the text field that appears after this option is selected.'
                )
              }}
            ></Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          {t('tireRepairScrap.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
